/**
 * ResetPassword - Public page for completing password reset
 *
 * Accessed via the link in the reset email: /reset-password?token=xxx
 * Validates token, shows form, sets new password.
 * Pattern mirrors AcceptInvite.tsx for consistency.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  validateResetToken,
  completePasswordReset,
  type ValidateResetResponse,
} from '@/lib/passwordResetApi';

type PageState = 'loading' | 'valid' | 'expired' | 'used' | 'invalid' | 'success' | 'error';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [validationData, setValidationData] = useState<ValidateResetResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setErrorMessage('No se proporcionó un token de restablecimiento válido.');
      return;
    }

    async function validate() {
      try {
        const data = await validateResetToken(token!);
        setValidationData(data);
        setPageState('valid');
      } catch (error: any) {
        console.error('[ResetPassword] Validation error:', error);
        const code = error.code || 'UNKNOWN_ERROR';
        if (code === 'RESET_EXPIRED') {
          setPageState('expired');
          setErrorMessage('Este enlace de restablecimiento ha expirado. Solicita uno nuevo.');
        } else if (code === 'RESET_ALREADY_USED') {
          setPageState('used');
          setErrorMessage('Este enlace ya fue utilizado. Si necesitas otro, solicítalo nuevamente.');
        } else {
          setPageState('invalid');
          setErrorMessage(error.message || 'Enlace de restablecimiento inválido.');
        }
      }
    }

    validate();
  }, [token]);

  const validatePassword = (): string | null => {
    if (!password) return 'La contraseña es requerida';
    if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
    if (password !== confirmPassword) return 'Las contraseñas no coinciden';
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    const validationError = validatePassword();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await completePasswordReset(token!, password);
      setPageState('success');
    } catch (error: any) {
      console.error('[ResetPassword] Completion error:', error);
      setFormError(error.message || 'Error al restablecer la contraseña');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validando enlace...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (pageState === 'expired' || pageState === 'used' || pageState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>
              {pageState === 'expired' && 'Enlace Expirado'}
              {pageState === 'used' && 'Enlace Ya Utilizado'}
              {pageState === 'invalid' && 'Enlace Inválido'}
            </CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Ir al Inicio
            </Button>
            {pageState === 'expired' && (
              <Button onClick={() => navigate('/forgot-password')}>
                Solicitar Nuevo Enlace
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle>Contraseña Restablecida</CardTitle>
            <CardDescription>
              Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/')}>
              Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid - show reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Nueva Contraseña</CardTitle>
          <CardDescription>
            Crea una nueva contraseña para tu cuenta
            {validationData?.email && (
              <span className="block mt-1 font-medium text-foreground">{validationData.email}</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {validationData && validationData.hoursRemaining > 0 && (
            <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>Este enlace expira en {validationData.hoursRemaining} horas</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="pl-9 pr-10"
                  disabled={isSubmitting}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirmar Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="confirm-new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="pl-9"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restableciendo...
                </>
              ) : (
                'Restablecer Contraseña'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
